import React, { useState } from 'react';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie, Settings, Shield } from 'lucide-react';

export function CookieConsent() {
  const { 
    showBanner, 
    preferences, 
    acceptAll, 
    acceptNecessary, 
    savePreferences,
    isSecureContext 
  } = useCookieConsent();
  
  const [showSettings, setShowSettings] = useState(false);
  const [tempPreferences, setTempPreferences] = useState(preferences);

  const handleSaveSettings = () => {
    savePreferences(tempPreferences);
    setShowSettings(false);
  };

  const handleOpenSettings = () => {
    setTempPreferences(preferences);
    setShowSettings(true);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur border-t border-border shadow-lg allow-pointer">
      <div className="container max-w-6xl mx-auto p-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 justify-between">
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <h3 className="font-semibold text-foreground">Gestion des cookies</h3>
              <p className="text-sm text-muted-foreground">
                Nous utilisons des cookies pour améliorer votre expérience. 
                Les cookies nécessaires sont requis pour le fonctionnement du site.
                {!isSecureContext && (
                  <span className="block text-orange-600 mt-1">
                    ⚠️ Connexion non sécurisée - Certaines fonctionnalités peuvent être limitées
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
            <Dialog open={showSettings} onOpenChange={setShowSettings}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" onClick={handleOpenSettings}>
                  <Settings className="h-4 w-4 mr-2" />
                  Paramètres
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md allow-pointer">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Paramètres des cookies
                  </DialogTitle>
                  <DialogDescription>
                    Choisissez quels types de cookies vous souhaitez autoriser.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="font-medium">Cookies nécessaires</Label>
                        <p className="text-xs text-muted-foreground">
                          Requis pour le fonctionnement du site
                        </p>
                      </div>
                      <Switch checked={true} disabled />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="font-medium">Cookies analytiques</Label>
                        <p className="text-xs text-muted-foreground">
                          Nous aident à comprendre l'utilisation du site
                        </p>
                      </div>
                      <Switch 
                        checked={tempPreferences.analytics}
                        onCheckedChange={(checked) => 
                          setTempPreferences(prev => ({ ...prev, analytics: checked }))
                        }
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="font-medium">Cookies marketing</Label>
                        <p className="text-xs text-muted-foreground">
                          Pour personnaliser le contenu et les publicités
                        </p>
                      </div>
                      <Switch 
                        checked={tempPreferences.marketing}
                        onCheckedChange={(checked) => 
                          setTempPreferences(prev => ({ ...prev, marketing: checked }))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" size="sm" onClick={() => setShowSettings(false)}>
                    Annuler
                  </Button>
                  <Button size="sm" onClick={handleSaveSettings} className="flex-1">
                    Enregistrer
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button variant="outline" size="sm" onClick={acceptNecessary}>
              Nécessaires seulement
            </Button>
            <Button size="sm" onClick={acceptAll}>
              Accepter tout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CookieSettingsButton() {
  const { resetConsent } = useCookieConsent();
  
  return (
    <Button 
      variant="ghost" 
      size="sm" 
      onClick={() => {
        resetConsent();
      }}
      className="text-muted-foreground hover:text-foreground"
    >
      <Cookie className="h-4 w-4 mr-2" />
      Gestion des cookies
    </Button>
  );
}

// Composant de debug pour tester la bannière de cookies
export function CookieDebugTools() {
  const { showBanner, resetConsent } = useCookieConsent();
  
  if (import.meta.env.PROD) return null;
  
  return (
    <div className="fixed top-4 right-4 z-[9999] pointer-events-auto bg-yellow-400 text-black p-3 rounded shadow-lg space-y-2 allow-pointer">
      <div className="font-medium">Cookie Banner: {showBanner ? 'VISIBLE' : 'HIDDEN'}</div>
      <Button size="sm" variant="secondary" onClick={() => {
        resetConsent();
      }}>
        Reset pour test
      </Button>
    </div>
  );
}